import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { SHAAlertDto } from 'app/shared/models/sha-alert.dto';

// SPEC-webplanner-052 / GAP-WEBPLANNER-068 — SHA presenter alert card (S11).
// Built to SHA-SPEC §3.1–§3.4 EXACTLY. No invented UI, no extra affordances.
//
// COPY DISCIPLINE (meet-UX CLAUDE.md hard rule): `message` and `script` are
// rendered VERBATIM from the SHAAlertDto payload — this component contains ZERO
// hardcoded SHA-SPEC §2 copy. If a payload arrives without message/script, the
// card renders nothing for that line rather than inventing filler.
//
// §3.4 BEHAVIOUR STATE MACHINE lives here, client-side:
//   • max 1 alert visible; highest severity wins (Red > Orange > Yellow)
//   • Dismiss → hidden + emits dismissed (view logs / calls hub DismissAlert)
//   • re-alert after 60s if the SAME signature is still arriving from upstream
//   • max 3 dismissals per signature/session → EXHAUSTED (no more re-alerts)
//   • 120s of no presenter action → auto-dismiss (emits autoDismissed)
//
// The component is fed the "current highest-severity live alert (or null)" via
// [alert]; the mounting view (S12, blocked on OQ-068-3) subscribes
// SessionHealthService.watchSession() into it. It is fully mountable +
// unit-testable in isolation without a live-session screen.

const RE_ALERT_MS = 60_000;
const AUTO_DISMISS_MS = 120_000;
const MAX_RE_ALERTS = 3;

const SEVERITY_RANK: Record<string, number> = { red: 3, orange: 2, yellow: 1 };
type Severity = 'yellow' | 'orange' | 'red';

interface SignatureState {
    /** how many times the presenter has dismissed this signature this session. */
    dismissals: number;
    /** true once dismissals >= MAX_RE_ALERTS — no more re-alerts (§3.4 EXHAUSTED). */
    exhausted: boolean;
    /** true while the alert is suppressed post-dismiss, before the 60s re-alert. */
    suppressed: boolean;
}

@Component({
    selector: 'app-sha-alert',
    templateUrl: './sha-alert.component.html',
    styleUrls: ['./sha-alert.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ShaAlertComponent implements OnChanges, OnDestroy {
    /**
     * Current highest-severity live presenter alert, or null when none.
     * SessionHealthService already reduces to the single worst alert; this
     * component enforces the §3.4 visibility rules on top.
     */
    @Input() alert: SHAAlertDto | null = null;

    /** Emitted when the presenter clicks Dismiss (view logs / calls hub). */
    @Output() dismissed = new EventEmitter<SHAAlertDto>();
    /** Emitted when the 120s no-action timeout fires (logged "presenter timed out"). */
    @Output() autoDismissed = new EventEmitter<SHAAlertDto>();

    /** The alert currently painted (null = nothing rendered). */
    visibleAlert: SHAAlertDto | null = null;

    /** Per-signature §3.4 state, keyed by signatureType for this session. */
    private readonly states = new Map<string, SignatureState>();

    private reAlertTimer: ReturnType<typeof setTimeout> | null = null;
    private autoDismissTimer: ReturnType<typeof setTimeout> | null = null;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['alert']) {
            this.evaluate(this.alert);
        }
    }

    ngOnDestroy(): void {
        this.clearTimers();
    }

    // ── §3.4 evaluation ───────────────────────────────────────────────────
    private evaluate(next: SHAAlertDto | null): void {
        if (!next) {
            // Upstream says no live alert → clear the card and its timers.
            this.hide();
            return;
        }

        const state = this.stateFor(next.signatureType);

        // EXHAUSTED: after 3 dismissals of this signature, never show it again.
        if (state.exhausted) {
            this.visibleAlert = null;
            return;
        }

        // Suppressed between a dismiss and the 60s re-alert: keep it hidden; the
        // re-alert timer will re-show it if the issue still persists.
        if (state.suppressed) {
            return;
        }

        // Highest-severity-wins: if a worse alert arrives, it replaces the shown
        // one (a strictly-lower-severity alert never displaces a live higher one).
        if (
            this.visibleAlert &&
            this.rank(next.severity) <= this.rank(this.visibleAlert.severity) &&
            next.signatureType !== this.visibleAlert.signatureType
        ) {
            return;
        }

        this.show(next);
    }

    private show(alert: SHAAlertDto): void {
        this.visibleAlert = alert;
        this.clearTimers();
        // 120s no-action auto-dismiss (§3.4).
        this.autoDismissTimer = setTimeout(() => this.onAutoDismiss(), AUTO_DISMISS_MS);
    }

    private hide(): void {
        this.visibleAlert = null;
        this.clearTimers();
    }

    /** Presenter clicked Dismiss. */
    onDismiss(): void {
        const alert = this.visibleAlert;
        if (!alert) { return; }

        const state = this.stateFor(alert.signatureType);
        state.dismissals += 1;
        state.suppressed = true;
        this.visibleAlert = null;
        this.clearTimers();

        if (state.dismissals >= MAX_RE_ALERTS) {
            // 3 dismissals → EXHAUSTED, no re-alert scheduled.
            state.exhausted = true;
            state.suppressed = false;
            this.dismissed.emit(alert);
            return;
        }

        // Re-alert after 60s IF the issue still persists (i.e. the same signature
        // is still the current upstream alert when the timer fires).
        this.reAlertTimer = setTimeout(() => {
            state.suppressed = false;
            if (this.alert && this.alert.signatureType === alert.signatureType) {
                this.evaluate(this.alert);
            }
        }, RE_ALERT_MS);

        this.dismissed.emit(alert);
    }

    private onAutoDismiss(): void {
        const alert = this.visibleAlert;
        if (!alert) { return; }
        this.visibleAlert = null;
        this.clearTimers();
        this.autoDismissed.emit(alert);
    }

    // ── view helpers ──────────────────────────────────────────────────────
    /** Normalised severity for CSS class + icon selection. */
    get severityClass(): Severity | '' {
        const s = (this.visibleAlert?.severity ?? '').toLowerCase();
        return s === 'red' || s === 'orange' || s === 'yellow' ? s : '';
    }

    /** §3.2 icon: Red → error circle; Yellow/Orange → warning triangle. */
    get isErrorCircle(): boolean {
        return this.severityClass === 'red';
    }

    // ── internals ─────────────────────────────────────────────────────────
    private stateFor(signatureType: string): SignatureState {
        let s = this.states.get(signatureType);
        if (!s) {
            s = { dismissals: 0, exhausted: false, suppressed: false };
            this.states.set(signatureType, s);
        }
        return s;
    }

    private rank(severity: string | null | undefined): number {
        return SEVERITY_RANK[(severity ?? '').toLowerCase()] ?? 0;
    }

    private clearTimers(): void {
        if (this.reAlertTimer) { clearTimeout(this.reAlertTimer); this.reAlertTimer = null; }
        if (this.autoDismissTimer) { clearTimeout(this.autoDismissTimer); this.autoDismissTimer = null; }
    }
}
